import Alert from '@/components/Alert/Alert'
import Countdown from '@/components/Countdown'
import GeeTest, { GeetestResultData } from '@/components/GeeTest'
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button'
import { Center } from '@/components/ui/center'
import { Input, InputField } from '@/components/ui/input'
import { VStack } from '@/components/ui/vstack'
import useDefaultToast from '@/components/useDefaultToast'
import { sendSMSCode } from '@/lib/api/sessionApi'
import { isValidMobile } from '@/lib/utils/isValidMobile'
import { passwordFreeLoginThunk } from '@/redux/slices/sessionSlice'
import { useAppDispatch } from '@/redux/store'
import { useRouter } from 'expo-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import handleLoginResult from './handleLoginResult'

export default function LoginViaSMS() {
  const { t } = useTranslation('login')
  const dispatch = useAppDispatch()
  const toast = useDefaultToast()

  const [mobile, setMobile] = useState('')
  const [code, setCode] = useState('')
  const [isCountDowning, setIsCountDowning] = useState(false)
  const [loading, setLoading] = useState(false)
  const router = useRouter()

  const isMobileValid = useMemo(() => {
    return isValidMobile(mobile)
  }, [mobile])

  const handleSendCodeButtonPress = async (geetestData: GeetestResultData) => {
    try {
      const response = await sendSMSCode(mobile, geetestData)
      console.log('response', response)
      if (!response.success) {
        return Alert.alert(response?.errors?.join(', ') ?? t('mobileLogin.sendCodeFailed'))
      }

      toast.show(t('mobileLogin.sendCodeSuccess'))
      setIsCountDowning(true)
    } catch (error) {
      console.error(error)
      Alert.alert(t('mobileLogin.sendCodeFailed'))
    }
  }

  const handleLoginButtonPress = async () => {
    setLoading(true)
    try {
      const result = await dispatch(passwordFreeLoginThunk({ mobile, code })).unwrap()
      handleLoginResult({ router, loginResult: result })
    } catch (error: any) {
      const message = error?.message || t('mobileLogin.codeVerificationFailed')
      Alert.alert(t('loginFailed'), message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Center>
      <VStack space="3xl" className="p-8 max-w-[540] w-full">
        <VStack space="lg">
          <Input size="lg">
            <InputField
              placeholder={t('mobileLogin.mobilePlaceholder')}
              value={mobile}
              onChangeText={setMobile}
              lineBreakModeIOS="clip"
            />
          </Input>
          <View className="relative">
            <Input size="lg">
              <InputField
                placeholder={t('mobileLogin.codePlaceholder')}
                value={code}
                onChangeText={setCode}
                maxLength={6}
                lineBreakModeIOS="clip"
              />
            </Input>
            <View className="absolute right-4 top-0 h-full justify-center">
              <GeeTest type="sms" onSuccess={handleSendCodeButtonPress}>
                {trigger => (
                  <Button variant="link" isDisabled={!isMobileValid || isCountDowning || loading} onPress={trigger}>
                    <ButtonText>
                      <Countdown initial={60} isRunning={isCountDowning} setIsRunning={setIsCountDowning}>
                        {value => (isCountDowning ? `${value}s` : t('mobileLogin.sendCode'))}
                      </Countdown>
                    </ButtonText>
                  </Button>
                )}
              </GeeTest>
            </View>
          </View>
        </VStack>
        <Button size="lg" onPress={handleLoginButtonPress} isDisabled={loading}>
          {loading && <ButtonSpinner />}
          <ButtonText>{t('signUpOrLogin')}</ButtonText>
        </Button>
      </VStack>
    </Center>
  )
}
